import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useBoard, type BoardFilters } from '@/features/project-board/BoardProvider';
import { BUCKETS, type Epic, type Issue, type Task } from '@/features/project-board/types';
import { useViewMode } from '@/shared/hooks/useViewMode';
import { ViewModeToggle, type ViewModeOption } from '@/shared/ui/ViewModeToggle';
import { ThroughputChart } from '@/features/project-board/components/ThroughputChart';
import { PERIOD_LABEL, type Period } from '@/features/project-board/analytics/throughput';
import { ActivityChart } from '@/features/project-board/components/ActivityChart';
import { DashboardPeriod } from '@/features/project-board/analytics/dashboard-period';
import { SourceTabs } from '@/features/project-board/components/SourceTabs';
import { EpicMembership, ItemKey } from '@/features/project-board/identity';
import { DashboardDate } from '@/features/project-board/dashboard-date';
import { QaCalendar, type QaCalendarSegment } from '@/features/project-board/qa-calendar';
import { DashboardQa } from '@/features/project-board/analytics/dashboard-qa';
import { DashboardEpicTitle } from '@/features/project-board/analytics/dashboard-epic-title';

/**
 * 대시보드 — "지금 무슨 일이 돌아가는가"를 한 화면에.
 * 색인된 데이터만 쓰고 프런트의 순수 집계 모듈이 계산한다(보드와 같은 인덱스라 숫자가 어긋나지 않는다).
 */
const PERIODS: ReadonlyArray<ViewModeOption<Period>> = [
  { key: 'day', label: '일', glyph: '·' },
  { key: 'week', label: '주', glyph: '▪' },
  { key: 'month', label: '월', glyph: '▬' },
];

type ActivityTab = 'throughput' | 'activity';

export function DashboardView(): React.JSX.Element {
  const { index, throughput: doneDays, loading, error, setFilters } = useBoard();
  const [period, setPeriod] = useViewMode<Period>('project-board.dashboard.period', 'week');
  const [activityTab, setActivityTab] = useState<ActivityTab>('activity');
  const defaultRange = DashboardDate.defaultRange(new Date());
  const [from, setFrom] = useState(defaultRange.from);
  const [to, setTo] = useState(defaultRange.to);
  const navigate = useNavigate();

  /** 카드에서 해당 화면으로 — 필터까지 걸어 보내야 "전체 보기"가 같은 목록을 보여준다. */
  const go = (to: string, patch: Partial<BoardFilters> = {}) => {
    setFilters({ query: '', owner: '', epic: '', onlyBlocked: false, ...patch });
    navigate(to);
  };

  /** 에픽 화면의 공유 링크 규약으로 해당 에픽 상세 드로어를 바로 연다. */
  const openEpic = (epic: Epic) => {
    setFilters({ query: '', owner: '', source: epic.source, epic: epic.slug, onlyBlocked: false });
    const params = new URLSearchParams({
      source: epic.source,
      epic: epic.slug,
      kind: 'epic',
      item: ItemKey.epic(epic),
    });
    navigate(`/project-board?${params.toString()}`);
  };

  if (error) return <Panel>불러오지 못했다 — {error}</Panel>;
  if (!index && loading) return <Panel>색인을 불러오는 중…</Panel>;
  if (!index) return <Panel>데이터가 없다.</Panel>;


  const { epics, tasks, issues } = index;
  const active = epics.filter((epic) => epic.donePct < 100 && epic.counts.progress + epic.counts.todo > 0);
  const scheduledEpics = DashboardQa.scheduledEpicsOf(epics);
  const qaStatusCounts = DashboardQa.ticketCountsOf(index.qaTickets);
  const openIssues = issues.filter((issue) => issue.status !== 'done');
  const openSplit = openIssueSplit(openIssues);
  // 팀 속도는 개인 합이 아니라 구간 처리량으로 본다. 날짜별 건수는 서버가 세어 준다.
  const periodMetrics = DashboardPeriod.of(tasks, issues, doneDays?.days ?? [], from, to, period, epics);
  const { throughput, weekday, activity, issueFlow, issueLead: lead } = periodMetrics;
  const currentAiDart = throughput.buckets.at(-1)?.aiDart ?? 0;
  const averageWindow = Math.min(4, Math.max(0, throughput.buckets.length - 1));
  const unit = PERIOD_LABEL[period];
  const donePct = tasks.length === 0 ? 0 : Math.round((count(tasks, 'done') / tasks.length) * 100);
  // 리뷰·E2E까지 갔는데 아직 안 끝난 에픽 = 검증 단계 병목.
  const verifying = epics.filter((epic) => ['review', 'e2e'].includes(epic.stage) && epic.donePct < 100);
  const stale = staleTasks(tasks, periodMetrics.toDate);
  const remaining = remainingEffort(tasks, epics);

  return (
    <div className="flex flex-col gap-5">
      <SourceTabs />

      <div className="flex flex-wrap items-center justify-end gap-2 rounded-lg border border-line bg-surface px-3 py-2">
        <span className="mr-auto text-[11px] text-muted">처리량·추세·정체·요일·이슈 지표 조회 기간</span>
        <label htmlFor="dashboard-from" className="text-xs font-semibold text-muted">
          시작일
        </label>
        <input
          id="dashboard-from"
          type="date"
          value={from}
          max={to}
          onChange={(event) => setFrom(event.target.value || defaultRange.from)}
          className="rounded-md border border-line bg-white px-2 py-1 text-xs text-ink outline-none focus:border-progress"
        />
        <span className="text-xs text-faint">~</span>
        <label htmlFor="dashboard-to" className="text-xs font-semibold text-muted">
          종료일
        </label>
        <input
          id="dashboard-to"
          type="date"
          value={to}
          min={from}
          max={defaultRange.to}
          onChange={(event) => setTo(event.target.value || defaultRange.to)}
          className="rounded-md border border-line bg-white px-2 py-1 text-xs text-ink outline-none focus:border-progress"
        />
        <button
          type="button"
          disabled={from === defaultRange.from && to === defaultRange.to}
          onClick={() => {
            setFrom(defaultRange.from);
            setTo(defaultRange.to);
          }}
          className="rounded-md border border-line px-2 py-1 text-xs text-muted hover:bg-line-soft disabled:cursor-default disabled:opacity-40"
        >
          초기화
        </button>
      </div>

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Stat label="에픽" value={epics.length} hint={`진행 중 ${active.length}`} to="/board" />
        <Stat label="task" value={tasks.length} hint={`대기 ${count(tasks, 'todo')} · 진행 ${count(tasks, 'progress')}`} to="/board/backlog" />
        <Stat label="완료율" value={donePct} suffix="%" hint={`완료 ${count(tasks, 'done')} / ${tasks.length}`} to="/board/backlog" />
        <Stat
          label="미처리 이슈"
          value={openIssues.length}
          hint={`전체 ${issues.length} · 완료 ${issues.length - openIssues.length}`}
          detail={`리뷰 미작성 ${openSplit.reviewPending.length} · 수정 미완 ${openSplit.unfixed.length}`}
          to="/board/issues"
        />
      </div>

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
        <Card
          title="진행 중 에픽"
          hint={`전체 ${active.length}건`}
          action={{ label: '에픽 보드에서 보기', onClick: () => go('/project-board') }}
        >
          <List
            rows={recent(active)
              .slice(0, 8)
              .map((epic) => ({
                key: `${epic.source}:${epic.slug}`,
                left: `${epic.year}-${epic.month}`,
                main: DashboardEpicTitle.of(epic),
                right: `${epic.stageLabel} · ${epic.donePct}%`,
                onClick: () => openEpic(epic),
              }))}
            empty="진행 중인 에픽이 없다."
          />
        </Card>

        <Card
          title="QA · 배포 일정"
          hint={`일정 등록 ${scheduledEpics.length}개 에픽 · QA 항목 ${index.qaTickets.length}건`}
          action={{ label: 'QA 일정에서 보기', onClick: () => go('/project-board/qa') }}
        >
          <div className="mb-3 flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-muted">
            <span>미실행 {qaStatusCounts['No Run'] ?? 0}</span>
            <span>실패 {qaStatusCounts.Fail ?? 0}</span>
            <span>차단 {qaStatusCounts.Block ?? 0}</span>
            <span>통과 {qaStatusCounts.Pass ?? 0}</span>
          </div>
          <DashboardScheduleCalendar
            epics={scheduledEpics}
            open={openEpic}
          />
        </Card>

      </div>

      <Card
        title="처리량 · 활동 추세"
        action={{ label: '백로그에서 보기', onClick: () => go('/project-board/backlog') }}
        hint={activityTab === 'throughput'
          ? `task + 이슈 완료 · ${unit.unit} 단위${throughput.undated > 0 ? ` · 날짜 없는 완료 ${throughput.undated}건 제외` : ''}`
          : `task 완료 · 에픽 문서 갱신 · 이슈 접수·처리 · ${from}~${to}`}
        toolbar={<ViewModeToggle options={PERIODS} value={period} onChange={setPeriod} />}
      >
        <div role="tablist" aria-label="처리량과 활동 추세" className="mb-4 flex items-end gap-5 border-b border-line">
          {([
            ['throughput', '처리량'],
            ['activity', '활동 추세'],
          ] as const).map(([key, label]) => (
            <button
              key={key}
              type="button"
              role="tab"
              aria-selected={activityTab === key}
              onClick={() => setActivityTab(key)}
              className={`-mb-px border-b-2 px-1 pb-2 pt-1 text-sm transition-colors ${
                activityTab === key
                  ? 'border-accent font-bold text-accent'
                  : 'border-transparent font-medium text-muted hover:text-ink'
              }`}
            >
              {label}
            </button>
          ))}
        </div>
        {activityTab === 'throughput' ? (
          <>
        <div className="mb-2 flex flex-wrap items-center gap-x-5 gap-y-1 text-[11px] text-faint">
          <span className="flex items-center gap-1.5">
            <i className="h-2.5 w-2 rounded-sm bg-done/60" /> 사람 처리
          </span>
          <span className="flex items-center gap-1.5">
            <i className="h-2.5 w-2 rounded-sm bg-progress/70" /> AI-DART 무인 처리
          </span>
        </div>
        <div className="mb-3 flex flex-wrap items-baseline gap-x-6 gap-y-2">
          <div>
            <div className="text-[11px] text-muted">기준 {unit.unit} 완료</div>
            <div className="text-2xl font-bold tabular-nums">{throughput.current}</div>
          </div>
          <div>
            <div className="text-[11px] text-muted">AI-DART 처리</div>
            <div className="text-2xl font-bold tabular-nums text-progress">{currentAiDart}</div>
          </div>
          <div>
            <div className="text-[11px] text-muted">{unit.previous} 대비</div>
            <div
              className={`text-2xl font-bold tabular-nums ${
                throughput.current >= throughput.previous ? 'text-done' : 'text-amber-700'
              }`}
            >
              {throughput.current >= throughput.previous ? '+' : ''}
              {throughput.current - throughput.previous}
              {throughput.changePct !== null && (
                <span className="ml-1 text-xs font-semibold">
                  ({throughput.changePct > 0 ? '+' : ''}
                  {throughput.changePct}%)
                </span>
              )}
            </div>
          </div>
          <div>
            <div className="text-[11px] text-muted">직전 {averageWindow}{unit.unit} 평균</div>
            <div className="text-2xl font-bold tabular-nums text-muted">{throughput.average4}</div>
          </div>
          <div className="ml-auto max-w-[340px] text-[11px] leading-relaxed text-faint">
            완료 시점은 task는 PROGRESS.md의 <code>Updated At</code>, 이슈는 마지막 처리 커밋 시각을 쓴
            근사값이다. 집계는 서버가 하고 화면은 구간으로 묶기만 한다.
          </div>
        </div>
        <ThroughputChart data={throughput} />
          </>
        ) : (
          <>
        <div className="mb-2 flex flex-wrap items-center gap-x-5 gap-y-1 text-[11px] text-faint">
          <span className="flex items-center gap-1.5">
            <i className="h-0.5 w-4 bg-done" /> 누적 완료 task <span className="text-done">(왼쪽 축)</span>
          </span>
          <span className="flex items-center gap-1.5">
            <i className="h-2.5 w-2 rounded-sm bg-accent/60" /> 에픽 문서 갱신
          </span>
          <span className="flex items-center gap-1.5">
            <i className="h-2.5 w-2 rounded-sm bg-progress/45" /> 이슈 접수{' '}
            <span className="text-progress">(오른쪽 축)</span>
          </span>
          <span className="flex items-center gap-1.5">
            <i className="h-2.5 w-2 rounded-sm bg-done/35" /> 이슈 처리
          </span>
          <span className="flex items-center gap-1.5">
            <i className="h-0.5 w-4 border-t border-dashed border-muted" /> 전체 task 스코프
          </span>
        </div>

        <ActivityChart data={activity} />

        <div className="mt-2 flex flex-wrap gap-x-8 gap-y-2">
          <Metric label="누적 완료" value={`${activity.doneTotal} / ${activity.scope}`} />
          <Metric label="에픽 문서 갱신" value={`${activity.points.reduce((sum, point) => sum + point.epicUpdates, 0)}건`} />
          <Metric label="최근 4주 완료 task" value={`${activity.points.slice(-4).reduce((sum, point) => sum + point.done, 0)}건`} />
          <Metric label="최근 4주 이슈 접수 → 처리" value={`${sumOf(issueFlow.slice(-4), 'opened')} → ${sumOf(issueFlow.slice(-4), 'closed')}`} />
          <Metric
            label="이슈 순증"
            value={`${sumOf(issueFlow.slice(-4), 'opened') - sumOf(issueFlow.slice(-4), 'closed')}건`}
          />
        </div>
        <p className="mt-2 text-[11px] leading-relaxed text-faint">
          task 완료와 에픽 문서 갱신은 PROGRESS·문서 갱신일 기준 근사값이다. QA는 항목별 상태 변경 이력이
          없어 아래에 현재 상태를 표시한다.
        </p>
        <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2 border-t border-line-soft pt-3 text-[11px]">
          <strong className="text-muted">QA 현재 상태</strong>
          <span>미실행 {qaStatusCounts['No Run'] ?? 0}</span>
          <span className="text-rose-700">실패 {qaStatusCounts.Fail ?? 0}</span>
          <span className="text-amber-700">차단 {qaStatusCounts.Block ?? 0}</span>
          <span className="text-done">통과 {qaStatusCounts.Pass ?? 0}</span>
          <span className="text-faint">N/A {qaStatusCounts['N/A'] ?? 0} · 건너뜀 {qaStatusCounts.Skip ?? 0}</span>
        </div>
          </>
        )}
      </Card>

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
        <Card
          title="상태 분포"
          hint={`${tasks.length}개 task`}
          action={{ label: '보드에서 보기', onClick: () => go('/project-board/kanban') }}
        >
          <div className="flex flex-col gap-2">
            {BUCKETS.map((bucket) => {
              const value = count(tasks, bucket.key);
              const pct = tasks.length === 0 ? 0 : Math.round((value / tasks.length) * 100);
              return (
                <div key={bucket.key} className="grid grid-cols-[64px_1fr_74px] items-center gap-2 text-xs">
                  <span className="text-muted">{bucket.label}</span>
                  <span className="h-2 overflow-hidden rounded-full bg-line-soft">
                    <i className={`block h-full rounded-full ${BAR[bucket.key]}`} style={{ width: `${pct}%` }} />
                  </span>
                  <span className="text-right tabular-nums">
                    <strong>{value}</strong> <span className="text-faint">{pct}%</span>
                  </span>
                </div>
              );
            })}
          </div>
        </Card>

        <Card
          title="정체 task"
          hint={`진행 중인데 14일+ 갱신 없음 · ${stale.length}건`}
          action={{ label: '전체 보기', onClick: () => go('/project-board/backlog') }}
        >
          <List
            rows={stale.slice(0, 8).map((task) => ({
              key: task.uid,
              left: `${task.epicSlug}-${task.taskId}`,
              main: task.titleHead || task.title,
              right: `${task.updatedAt || '갱신 없음'}${task.owner ? ` · ${task.owner}` : ''}`,
              onClick: () => go('/project-board/backlog', { query: task.taskId, epic: task.epicSlug }),
            }))}
            empty="정체된 task가 없다."
          />
        </Card>

        <Card
          title="검증 대기 에픽"
          hint={`리뷰·E2E 단계에서 미완 · ${verifying.length}건`}
          action={{ label: '전체 보기', onClick: () => go('/project-board') }}
        >
          <List
            rows={verifying.slice(0, 8).map((epic) => ({
              key: epic.slug,
              left: `${epic.year}-${epic.month}`,
              main: epic.title,
              right: `${epic.stageLabel} · ${epic.donePct}%`,
              onClick: () => openEpic(epic),
            }))}
            empty="검증 대기 에픽이 없다."
          />
        </Card>

        <Card
          title="요일별 처리량"
          hint={`주말 ${weekday.weekendDone}건 · 전체의 ${weekday.weekendPct}%`}
        >
          <div className="flex items-end gap-2">
            {weekday.days.map((day) => {
              const max = Math.max(1, ...weekday.days.map((candidate) => candidate.done));
              return (
                <div key={day.label} className="flex flex-1 flex-col items-center gap-1">
                  <span className="text-[11px] tabular-nums text-muted">{day.done}</span>
                  <span
                    title={`${day.label}요일 · 완료 ${day.done}건`}
                    style={{ height: `${Math.max(4, (day.done / max) * 96)}px` }}
                    className={`w-full rounded-t ${day.weekend ? 'bg-amber-400' : 'bg-done/60'}`}
                  />
                  <span className={`text-[11px] ${day.weekend ? 'font-bold text-amber-700' : 'text-muted'}`}>
                    {day.label}
                  </span>
                </div>
              );
            })}
          </div>
          <div className="mt-3 flex flex-wrap gap-x-8 gap-y-2">
            <Metric label="주말 완료" value={`${weekday.weekendDone}건`} />
            <Metric label="주말 비중" value={`${weekday.weekendPct}%`} />
            <Metric label={`최근 ${weekday.recentWeeks}주 주말`} value={`${weekday.recentWeekendDone}건`} />
          </div>
          <p className="mt-2 text-[11px] leading-relaxed text-faint">
            상태가 바뀐 날짜의 요일로 센다. 토·일 갱신을 주말 작업으로 보되, 문서를 몰아서 갱신한 경우도
            섞일 수 있어 정확한 근무 기록은 아니다.
          </p>
        </Card>

        <Card title="repo별 진행" hint={`${repoRows(tasks).length}개 repo`}>
          <ul className="flex flex-col gap-1.5">
            {repoRows(tasks)
              .slice(0, 7)
              .map((row) => (
                <li
                  key={row.repo}
                  onClick={() => go('/project-board/backlog', { query: row.repo })}
                  className="grid cursor-pointer grid-cols-[132px_1fr_84px] items-center gap-2 rounded text-xs hover:bg-line-soft"
                  title={`${row.repo} task ${row.total}건`}
                >
                  <span className="truncate font-mono text-[11px]">{row.repo}</span>
                  <span className="h-2 overflow-hidden rounded-full bg-line-soft">
                    <i className="block h-full rounded-full bg-done" style={{ width: `${row.donePct}%` }} />
                  </span>
                  <span className="text-right tabular-nums">
                    <strong>{row.donePct}%</strong> <span className="text-faint">/{row.total}</span>
                  </span>
                </li>
              ))}
          </ul>
        </Card>

        <Card title="이슈 처리 소요" hint={`완료 ${lead.sample}건 기준`}>
          <div className="flex flex-wrap gap-x-8 gap-y-3">
            <Metric label="평균" value={`${lead.average}일`} />
            <Metric label="중위" value={`${lead.median}일`} />
            <Metric label="최장" value={`${lead.longest}일`} />
            <Metric label="기준월 접수 → 처리" value={`${lead.openedThisMonth} → ${lead.closedThisMonth}`} />
          </div>
          <p className="mt-3 text-[11px] leading-relaxed text-faint">
            접수일(폴더 날짜)부터 마지막 처리 커밋까지의 일수다. 이슈 문서에 완료 시각 필드가 없어 저장소
            이력을 쓴다.
          </p>
        </Card>

        <Card
          title="미처리 이슈"
          hint={`수정 미완 ${openSplit.unfixed.length}건 · 리뷰 미작성 ${openSplit.reviewPending.length}건`}
          action={{ label: '전체 보기', onClick: () => go('/project-board/issues', { hideDoneIssues: true }) }}
        >
          <List
            rows={[...openSplit.unfixed, ...openSplit.reviewPending].slice(0, 8).map((issue) => ({
              key: issue.id,
              left: issue.reportedAt,
              main: issue.titleHead,
              right: `${issue.stageLabel}${issue.handledBy ? ` · ${issue.handledBy}` : ''}`,
              onClick: () => go('/project-board/issues', { hideDoneIssues: true, query: issue.id }),
            }))}
            empty="미처리 이슈가 없다."
          />
          <p className="mt-3 text-[11px] leading-relaxed text-faint">
            리뷰 미작성은 수정·검증까지 끝났는데 <code>review.md</code>가 템플릿으로 남은 이슈다. 코드
            작업이 끝나도 완료로 넘어가지 않아 총계가 멈춘 것처럼 보인다 — 밀린 리뷰를 채워야 줄어든다.
          </p>
        </Card>
        <Card
          title="에픽별 잔여 공수"
          hint={`미완 ${remaining.taskCount}건 · 합계 ${remaining.totalHours}h (≈${remaining.totalDays}일)${
            remaining.missing > 0 ? ` · 공수 미기재 ${remaining.missing}건 제외` : ''
          }`}
          action={{ label: '전체 보기', onClick: () => go('/project-board') }}
        >
          <ul className="flex flex-col gap-1.5">
            {remaining.rows.slice(0, 8).map((row) => (
              <li
                key={row.slug}
                onClick={() => {
                  const epic = epics.find((candidate) => candidate.slug === row.slug);
                  if (epic) openEpic(epic);
                }}
                className="grid cursor-pointer grid-cols-[1fr_120px_110px] items-center gap-2 rounded text-xs hover:bg-line-soft"
                title={`${row.title} · 미완 ${row.taskCount}건`}
              >
                <span className="truncate">{row.title}</span>
                <span className="h-2 overflow-hidden rounded-full bg-line-soft">
                  <i
                    className="block h-full rounded-full bg-progress"
                    style={{ width: `${Math.round((row.hours / Math.max(1, remaining.rows[0].hours)) * 100)}%` }}
                  />
                </span>
                <span className="text-right tabular-nums">
                  <strong>{row.hours}h</strong> <span className="text-faint">/{row.taskCount}건</span>
                </span>
              </li>
            ))}
          </ul>
          <p className="mt-2 text-[11px] leading-relaxed text-faint">
            미완(대기·진행) task의 예상 공수 합이다. 계획 표에 공수가 없는 task는 빠지므로 **하한**으로 읽는다.
          </p>
        </Card>

      </div>

      <Card
        title="최근 갱신 에픽"
        hint="갱신일 기준"
        action={{ label: '전체 보기', onClick: () => go('/project-board') }}
      >
        <List
          rows={recent(epics)
            .slice(0, 8)
            .map((epic) => ({
              key: epic.slug,
              left: epic.updatedAt ?? '-',
              main: epic.title,
              right: `${epic.stageLabel} · ${epic.donePct}%`,
              onClick: () => openEpic(epic),
            }))}
          empty="갱신 기록이 없다."
        />
      </Card>
    </div>
  );
}

const SCHEDULE_TONES: ReadonlyArray<{ review: string; release: string }> = [
  { review: 'bg-sky-100 text-sky-900', release: 'border-sky-500 text-sky-800' },
  { review: 'bg-amber-100 text-amber-900', release: 'border-amber-500 text-amber-800' },
  { review: 'bg-emerald-100 text-emerald-900', release: 'border-emerald-500 text-emerald-800' },
  { review: 'bg-violet-100 text-violet-900', release: 'border-violet-500 text-violet-800' },
  { review: 'bg-rose-100 text-rose-900', release: 'border-rose-500 text-rose-800' },
  { review: 'bg-teal-100 text-teal-900', release: 'border-teal-500 text-teal-800' },
];

function DashboardScheduleCalendar({
  epics,
  open,
}: {
  epics: Epic[];
  open: (epic: Epic) => void;
}): React.JSX.Element {
  const today = QaCalendar.today();
  const [month, setMonth] = useState(() => QaCalendar.monthOf(today));
  const events = QaCalendar.eventsOf(epics);
  const weeks = QaCalendar.weeksOf(month);
  const buttonClass = 'rounded-md border border-line bg-surface px-2 py-1 text-[11px] hover:bg-line-soft';

  return (
    <div className="overflow-hidden rounded-md border border-line">
      <div className="flex items-center gap-2 border-b border-line px-2 py-1.5">
        <button type="button" aria-label="이전 달" onClick={() => setMonth((value) => QaCalendar.shiftMonth(value, -1))} className={buttonClass}>‹</button>
        <strong className="min-w-[88px] text-center text-xs">{month.replace('-', '년 ')}월</strong>
        <button type="button" aria-label="다음 달" onClick={() => setMonth((value) => QaCalendar.shiftMonth(value, 1))} className={buttonClass}>›</button>
        <button type="button" onClick={() => setMonth(QaCalendar.monthOf(today))} className={buttonClass}>이번 달</button>
        <span className="ml-auto hidden text-[10px] text-muted sm:inline">막대: QA 기간 · 테두리: 배포일</span>
      </div>
      <div className="grid grid-cols-7 border-b border-line-soft text-center text-[10px] font-semibold text-muted">
        {['월', '화', '수', '목', '금', '토', '일'].map((day, index) => (
          <div key={day} className={`py-1 ${index > 4 ? 'bg-canvas' : ''}`}>{day}</div>
        ))}
      </div>
      {weeks.map((week) => (
        <DashboardScheduleWeek key={week[0]} week={week} month={month} today={today} events={events} open={open} />
      ))}
      <div className="flex gap-3 border-t border-line-soft px-2 py-1 text-[10px] text-muted sm:hidden">
        <span>색 막대: QA 기간</span><span>테두리: 배포일</span>
      </div>
      {events.length === 0 && <p className="border-t border-line-soft px-2 py-2 text-center text-[11px] text-faint">등록된 일정이 없다.</p>}
    </div>
  );
}

function DashboardScheduleWeek({
  week,
  month,
  today,
  events,
  open,
}: {
  week: string[];
  month: string;
  today: string;
  events: ReturnType<typeof QaCalendar.eventsOf>;
  open: (epic: Epic) => void;
}): React.JSX.Element {
  const segments = QaCalendar.segmentsOf(week, events);
  const lanes = segments.reduce((max, segment) => Math.max(max, segment.lane + 1), 0);

  return (
    <div className="relative grid min-h-[74px] grid-cols-7 border-b border-line-soft last:border-b-0" style={{ gridTemplateRows: `20px ${'18px '.repeat(lanes)}1fr` }}>
      {week.map((date, index) => (
        <div
          key={date}
          className={`border-r border-line-soft last:border-r-0 ${index > 4 ? 'bg-canvas' : ''}`}
          style={{ gridColumn: index + 1, gridRow: `1 / span ${lanes + 2}` }}
        >
          <div className={`px-1 pt-0.5 text-right text-[10px] ${
            date === today ? 'font-bold text-accent' : QaCalendar.monthOf(date) === month ? 'text-ink' : 'text-faint'
          }`}>
            {Number(date.slice(8))}
          </div>
        </div>
      ))}
      {segments.map((segment: QaCalendarSegment) => {
        const review = segment.event.kind === 'review';
        const tone = SCHEDULE_TONES[QaCalendar.toneOf(segment.event.epic, SCHEDULE_TONES.length)];
        return (
          <button
            key={`${segment.event.kind}:${segment.event.epic.source}:${segment.event.epic.slug}`}
            type="button"
            onClick={() => open(segment.event.epic)}
            title={`${DashboardEpicTitle.of(segment.event.epic)} · ${review ? `QA ${segment.event.start} ~ ${segment.event.end}` : `배포 ${segment.event.start}`}`}
            className={`mx-0.5 my-px cursor-pointer truncate px-1 text-left text-[9px] leading-4 hover:brightness-95 sm:text-[10px] ${
              review ? tone.review : `border bg-surface ${tone.release}`
            } ${segment.continuesBefore ? 'rounded-l-none' : 'rounded-l'} ${segment.continuesAfter ? 'rounded-r-none' : 'rounded-r'}`}
            style={{ gridColumn: `${segment.column + 1} / span ${segment.span}`, gridRow: segment.lane + 2 }}
          >
            {review ? 'QA: ' : '배포: '}{DashboardEpicTitle.of(segment.event.epic)}
          </button>
        );
      })}
    </div>
  );
}

const BAR: Record<string, string> = {
  todo: 'bg-todo',
  progress: 'bg-progress',
  done: 'bg-done',
  dropped: 'bg-dropped',
  unknown: 'bg-unknown',
};

function Panel({ children }: { children: React.ReactNode }): React.JSX.Element {
  return <div className="rounded-lg border border-dashed border-line bg-surface p-10 text-center text-muted">{children}</div>;
}

function Stat({
  label,
  value,
  hint,
  detail,
  to,
  tone,
  suffix,
}: {
  label: string;
  value: number;
  hint: string;
  /** 총계가 왜 그 값인지 갈라 보여야 할 때만 쓰는 둘째 줄. */
  detail?: string;
  to: string;
  tone?: 'warn';
  suffix?: string;
}): React.JSX.Element {
  return (
    <Link
      to={to}
      className="rounded-lg border border-line bg-surface px-4 py-3 transition hover:border-accent hover:shadow-sm"
    >
      <div className="text-[11px] font-semibold tracking-wide text-muted uppercase">{label}</div>
      <div className={`mt-1 text-2xl font-bold tabular-nums ${tone === 'warn' && value > 0 ? 'text-amber-700' : ''}`}>
        {value}
        {suffix && <span className="text-base">{suffix}</span>}
      </div>
      <div className="text-[11px] text-faint">{hint}</div>
      {detail && <div className="text-[11px] text-faint">└ {detail}</div>}
    </Link>
  );
}

function Card({
  title,
  hint,
  action,
  toolbar,
  children,
}: {
  title: string;
  hint: string;
  /** 해당 화면으로 이동하는 버튼. 카드는 요약만 보여주므로 전체 목록으로 가는 길이 항상 있어야 한다. */
  action?: { label: string; onClick: () => void };
  /** 카드가 제공하는 도구(기간 전환 등). */
  toolbar?: React.ReactNode;
  children: React.ReactNode;
}): React.JSX.Element {
  return (
    <section className="rounded-lg border border-line bg-surface p-4">
      <header className="mb-3 flex flex-wrap items-baseline gap-2">
        <h2 className="text-[13px] font-bold">{title}</h2>
        <span className="text-[11px] text-faint">{hint}</span>
        {toolbar && <span className="ml-2">{toolbar}</span>}
        {action && (
          <button
            onClick={action.onClick}
            className="ml-auto cursor-pointer text-[11.5px] text-accent hover:underline"
          >
            {action.label} →
          </button>
        )}
      </header>
      {children}
    </section>
  );
}

function List({
  rows,
  empty,
}: {
  rows: Array<{ key: string; left: string; main: string; right: string; onClick?: () => void }>;
  empty: string;
}): React.JSX.Element {
  if (rows.length === 0) return <div className="text-xs text-faint">{empty}</div>;
  return (
    <ul className="flex flex-col">
      {rows.map((row) => (
        <li
          key={row.key}
          onClick={row.onClick}
          className={`grid grid-cols-[130px_1fr_auto] items-center gap-2 border-b border-line-soft py-1.5 text-xs last:border-0 ${
            row.onClick ? 'cursor-pointer hover:bg-line-soft' : ''
          }`}
        >
          <span className="truncate font-mono text-[11px] text-faint">{row.left}</span>
          <span className="truncate">{row.main}</span>
          <span className="text-right text-[11px] text-muted">{row.right}</span>
        </li>
      ))}
    </ul>
  );
}

function sumOf(points: Array<{ opened: number; closed: number }>, key: 'opened' | 'closed'): number {
  return points.reduce((sum, point) => sum + point[key], 0);
}

/** 미완 task의 예상 공수 합. 공수가 없는 task는 셀 수 없어 하한으로만 쓴다. */
function remainingEffort(
  tasks: Task[],
  epics: Epic[],
): {
  rows: Array<{ slug: string; title: string; hours: number; taskCount: number }>;
  totalHours: number;
  totalDays: number;
  taskCount: number;
  missing: number;
} {
  const open = tasks.filter((task) => task.status === 'todo' || task.status === 'progress');
  const rows: Array<{ slug: string; title: string; hours: number; taskCount: number }> = [];

  for (const task of open) {
    const row = rows.find((candidate) => candidate.slug === task.epicSlug);
    const target =
      row ??
      {
        slug: task.epicSlug,
        title: EpicMembership.epicOf(epics, task)?.title ?? task.epicSlug,
        hours: 0,
        taskCount: 0,
      };
    if (!row) rows.push(target);
    target.taskCount += 1;
    target.hours += task.estimateHours ?? 0;
  }

  const totalHours = Math.round(rows.reduce((sum, row) => sum + row.hours, 0));
  return {
    rows: rows.map((row) => ({ ...row, hours: Math.round(row.hours) })).sort((left, right) => right.hours - left.hours),
    totalHours,
    // 8시간 = 1일. 하네스 estimate 표기(`1d`)와 같은 환산이다.
    totalDays: Math.round(totalHours / 8),
    taskCount: open.length,
    missing: open.filter((task) => task.estimateHours === null).length,
  };
}

function Metric({ label, value }: { label: string; value: string }): React.JSX.Element {
  return (
    <div>
      <div className="text-[11px] text-muted">{label}</div>
      <div className="text-xl font-bold tabular-nums">{value}</div>
    </div>
  );
}

/** 진행 중인데 오래 갱신이 없는 task — 막힌 것보다 "멈춰 있는 것"이 더 행동을 요구한다. */
function staleTasks(tasks: Task[], now: Date, days = 14): Task[] {
  const limit = new Date(now.getTime() - days * 86_400_000).toISOString().slice(0, 10);
  return tasks
    .filter((task) => task.status === 'progress')
    .filter((task) => task.updatedAt === '' || task.updatedAt.slice(0, 10) < limit)
    .sort((left, right) => left.updatedAt.localeCompare(right.updatedAt));
}

function repoRows(tasks: Task[]): Array<{ repo: string; total: number; done: number; donePct: number }> {
  const rows: Array<{ repo: string; total: number; done: number; donePct: number }> = [];
  for (const task of tasks) {
    if (!task.repo) continue;
    const row = rows.find((candidate) => candidate.repo === task.repo);
    const target = row ?? { repo: task.repo, total: 0, done: 0, donePct: 0 };
    if (!row) rows.push(target);
    target.total += 1;
    if (task.status === 'done') target.done += 1;
  }
  for (const row of rows) row.donePct = Math.round((row.done / row.total) * 100);
  return rows.sort((left, right) => right.total - left.total);
}

/**
 * 미처리 이슈를 "리뷰 문서만 안 쓴 것"과 "수정이 안 끝난 것"으로 가른다.
 *
 * 완료 판정이 review 단계에 걸려 있어(`Issue.statusOf`), 수정·검증까지 끝내 놓고 리뷰를 안 쓴 이슈는
 * 아무 일이 없어도 미처리로 남는다. 이 둘을 합쳐서만 보여주면 총계가 고장 난 것처럼 읽힌다.
 */
function openIssueSplit(openIssues: Issue[]): { reviewPending: Issue[]; unfixed: Issue[] } {
  return {
    reviewPending: openIssues.filter(reviewPending),
    unfixed: openIssues.filter((issue) => !reviewPending(issue)),
  };
}

/** 수정 단계(fix-result·validation)가 다 채워졌는데 미처리면 남은 관문은 리뷰뿐이다. */
function reviewPending(issue: Issue): boolean {
  return issue.stages.find((stage) => stage.key === 'execution')?.fill === 'filled';
}

function count(tasks: Task[], status: string): number {
  return tasks.filter((task) => task.status === status).length;
}

function recent(epics: Epic[]): Epic[] {
  return [...epics].sort((left, right) => (right.updatedAt ?? '').localeCompare(left.updatedAt ?? ''));
}

export type { Issue };
